<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-container fluid>
          <v-row v-if="!sendMode">
            <v-col>
              <p>
                Do you want to send an engagement letter?
              </p>
              <v-radio-group
                v-model="shouldSendLetter"
                row
                :error-messages="getErrorMessages(v$.shouldSendLetter)"
                @input="v$.shouldSendLetter.$touch()"
                @blur="v$.shouldSendLetter.$touch()"
              >
                <v-radio
                  label="Yes"
                  value="true"
                />
                <v-radio
                  label="No"
                  value="false"
                />
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row v-if="shouldDisplayForm">
            <v-col v-if="isInitializing">
              <div
                class="d-flex justify-center align-center"
                style="min-height: 200px;"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="50"
                />
                <span class="ml-3">Loading...</span>
              </div>
            </v-col>
            <v-col v-else-if="showEngagementLetterForm">
              <v-alert
                v-if="organizationWarning"
                type="warning"
                outlined
                class="mb-4"
              >
                {{ organizationWarning }}
              </v-alert>

              <v-row>
                <v-col cols="6">
                  <v-select
                    v-model="edited.engagement_letter.template_id"
                    outlined
                    item-text="title"
                    item-value="id"
                    persistent-hint
                    :items="engagementLetterTemplates"
                    label="Template *"
                    :error-messages="getErrorMessages(v$.edited.engagement_letter.template_id)"
                    @input="v$.edited.engagement_letter.template_id.$touch()"
                    @blur="v$.edited.engagement_letter.template_id.$touch()"
                    @change="onTemplateChange"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="edited.engagement_letter.language_id"
                    outlined
                    item-text="name"
                    item-value="id"
                    persistent-hint
                    :items="availableLanguages"
                    label="Language *"
                    :error-messages="getErrorMessages(v$.edited.engagement_letter.language_id)"
                    :disabled="!edited.engagement_letter.template_id"
                    @input="v$.edited.engagement_letter.language_id.$touch()"
                    @blur="v$.edited.engagement_letter.language_id.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row v-if="shouldDisplayTemplate">
                <v-col>
                  <v-card>
                    <v-app-bar
                      flat
                      color="rgba(0, 0, 0, .75)"
                    >
                      <v-toolbar-title class="text-subtitle-1 white--text pl-0">
                        Engagement Letter Preview
                      </v-toolbar-title>
                    </v-app-bar>
                    <v-card-title>{{ legalCaseType.name }}</v-card-title>
                    <v-card-subtitle>{{ legalCaseType.description }}</v-card-subtitle>
                    <v-card-text style="max-height: 400px; overflow: scroll">
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <p v-html="letterContent" />
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-radio-group
                    v-model="edited.engagement_letter.notification_channel"
                    row
                    :error-messages="getErrorMessages(v$.edited.engagement_letter.notification_channel)"
                    @input="v$.edited.engagement_letter.notification_channel.$touch()"
                    @blur="v$.edited.engagement_letter.notification_channel.$touch()"
                  >
                    <v-radio
                      :disabled="!isTwilioSettingsSetup"
                      :label="phoneLabel"
                      value="text"
                    />
                    <v-radio
                      :disabled="!emailNotificationEnabled"
                      :label="emailLabel"
                      value="email"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row v-if="edited.engagement_letter.notification_channel">
                <v-col>
                  <v-select
                    v-model="edited.engagement_letter.recipient_identifier"
                    :items="partiesOptions"
                    label="Recipient *"
                    outlined
                    persistent-hint
                    :error-messages="getErrorMessages(v$.edited.engagement_letter.recipient_identifier)"
                    @input="v$.edited.engagement_letter.recipient_identifier.$touch()"
                    @blur="v$.edited.engagement_letter.recipient_identifier.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row v-if="edited.engagement_letter.recipient_identifier">
                <v-col>
                  <v-select
                    v-model="edited.engagement_letter.notification_language"
                    :items="notificationLanguages"
                    label="Language of the notification *"
                    outlined
                    persistent-hint
                    :error-messages="getErrorMessages(v$.edited.engagement_letter.notification_language)"
                    @input="v$.edited.engagement_letter.notification_language.$touch()"
                    @blur="v$.edited.engagement_letter.notification_language.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row v-if="edited.engagement_letter.notification_language">
                <v-col>
                  <blockquote>
                    {{ edited.engagement_letter.notification_message }}
                  </blockquote>
                </v-col>
              </v-row>
            </v-col>
            <v-row v-else-if="hasOrganizationError">
              <v-col>
                <v-alert
                  type="error"
                  outlined
                  class="mb-4"
                >
                  <strong>Cannot send engagement letter:</strong> {{ organizationError }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-else-if="noEngagementLetters">
              <v-col>
                <p>
                  No engagement letter templates are available. Please contact your administrator to create engagement letter templates in the settings.
                </p>
              </v-col>
            </v-row>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { helpers, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { isEmpty } from 'lodash'
import { useVuelidate } from '@vuelidate/core'

import { alertHandler } from '@/alertHandler'
import { partyOptionLabel } from '@/utilities'

import { ENGAGEMENT_LETTER_TEMPLATE_NAMESPACE } from '@/store/engagement-letter-template'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

const ENGAGEMENT_LETTER = {
  language_id: null,
  notification_channel: null,
  notification_language: null,
  recipient_identifier: null,
  template_id: null,
}

export default {
  name: 'LegalCaseEditorEngagementLetter',

  props: {
    // ESLint will think `loading` is not used because it is not directly used in this component per se. However, it
    // is updated in this component, cf. watcher `isInitializing`.
    // eslint-disable-next-line vue/no-unused-properties
    loading: {
      default: true,
      type: Boolean,
    },

    // This is set to true to recognize when this component is used standalone, i.e. usually outside of a case
    // creation wizard. Use cases can be found in Legal Case toolbar via the `Send engagement letter` action.
    sendMode: {
      default: false,
      type: Boolean,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      isInitializing: true,

      notificationLanguages: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' },
      ],

      parties: [],
      shouldSendLetter: 'false',
    }
  },

  asyncComputed: {
    async primaryAttorney () {
      if (!this.edited.primary_attorney_id) return

      return await this.getUser(this.edited.primary_attorney_id)
    },
  },

  computed: {
    allLanguages () {
      return this.languagesOptions?.map(lang => ({ id: lang.value, name: lang.text })) || []
    },

    availableLanguages () {
      if (!this.selectedTemplate) return []
      return this.selectedTemplate.engagement_letter_template_translations.map(translation => {
        const language = this.allLanguages?.find(lang => lang.id === translation.language_id)
        return language ? { id: language.id, name: language.name } : null
      }).filter(Boolean)
    },

    emailLabel () {
      let label = 'Send by email'
      if (!this.emailNotificationEnabled) label += ' (Notifications by email are not enabled.)'
      return label
    },

    hasOrganizationError () {
      if (this.isInitializing) return false
      // Only consider organization error if we're actually sending a letter
      if (this.shouldSendLetter !== 'true') return false
      return !this.primaryAttorney || !this.organizationName
    },

    isFormValid () {
      // Form is valid if Vuelidate validation passes and no organization errors (when applicable)
      const vuelidateValid = !this.v$.$invalid
      const organizationValid = !this.hasOrganizationError

      return vuelidateValid && organizationValid
    },

    isTwilioSettingsSetup () {
      return !!this.features?.twilio?.enabled
    },

    legalCaseType () {
      if (!this.edited) return

      return this.legalCaseTypesOptions?.find(l => l.value === this.edited.legal_case_type_id)
    },

    letterContent () {
      if (!this.selectedTemplate || !this.edited.engagement_letter.language_id) return ''

      const translation = this.selectedTemplate.engagement_letter_template_translations?.find(
        t => t.language_id === this.edited.engagement_letter.language_id
      )

      if (!translation) return ''

      let content = translation.content || ''

      // Replace organization placeholder if we have organization name
      if (this.organizationName) {
        content = content.replace(/\[Organization\]/g, this.organizationName)
      }

      if (this.legalCaseType.name) {
        content = content.replace(/\[LegalCaseType\]/g, this.legalCaseType.name)
      }

      return content.replaceAll('\n', '<br />')
    },

    noEngagementLetters () {
      return this.engagementLetterTemplates.length === 0
    },

    notificationContent () {
      let content

      switch (this.edited.engagement_letter.notification_language) {
        case 'en':
          content = `
           Hello! A new case ${this.legalCaseType.name} ID: {legal_case_id} has been created for you by provider ${this.primaryAttorney.first_name} ${this.primaryAttorney.last_name}. You must sign a digital engagement to continue. Contact the provider prior to signing if you have questions at ${this.primaryAttorney.email}. Please open the link, review and sign by typing your name. {link}
`
          break
        case 'es':
          content = `
          ¡Hola! El proveedor ${this.primaryAttorney.first_name} ${this.primaryAttorney.last_name} ha creado un nuevo caso ${this.legalCaseType.name} con ID {legal_case_id}. Debe firmar un compromiso digital para continuar. Comuniquese con el proveedor al email ${this.primaryAttorney.email} antes de firmar si tiene preguntas. Abra el enlace, revise y firme escribiendo su nombre. {link}
`
          break
      }

      return content
    },

    organizationError () {
      if (!this.primaryAttorney) {
        return 'Cannot load primary attorney information. Please ensure a primary attorney is assigned to this case.'
      }
      if (!this.organizationName) {
        return 'Primary attorney does not have an organization name set in their profile.'
      }
      return null
    },

    organizationName () {
      if (!this.primaryAttorney) return null
      return this.primaryAttorney.organization_name
    },

    organizationWarning () {
      if (this.isInitializing) return null
      if (!this.primaryAttorney) {
        return 'Cannot load primary attorney information. Please ensure a primary attorney is assigned to this case.'
      }
      if (!this.organizationName) {
        return 'Primary attorney does not have an organization name set in their profile. The [Organization] variable in templates will be empty.'
      }
      return null
    },

    partiesOptions () {
      return this.parties.map(party => ({
        disabled: !this.partyCanBeRecipient(party),
        text: partyOptionLabel.bind(this)(party),
        value: party.identifier,
      }))
    },

    phoneLabel () {
      let label = 'Send by text'
      if (!this.isTwilioSettingsSetup) label += ' (Twilio is not set up. You can not send a text.)'
      return label
    },

    selectedTemplate () {
      if (!this.edited.engagement_letter.template_id) return null
      return this.templateById(this.edited.engagement_letter.template_id)
    },

    shouldDisplayForm () {
      return this.shouldSendLetter === 'true'
    },

    shouldDisplayTemplate () {
      return this.edited.engagement_letter.language_id && this.edited.legal_case_type_id
    },

    showEngagementLetterForm () {
      return !this.noEngagementLetters && !this.hasOrganizationError
    },

    ...mapGetters([
      'languagesOptions',
      'legalCaseTypesOptions',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'edited',
      'emailNotificationEnabled',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),

    ...mapGetters(ENGAGEMENT_LETTER_TEMPLATE_NAMESPACE, [
      'engagementLetterTemplates',
      'templateById',
    ]),
  },

  validations () {
    return {
      edited: {
        engagement_letter: {
          language_id: {
            required: helpers.withMessage('Language is required',
              requiredIf(_ => this.shouldSendLetter === 'true' && this.edited.engagement_letter.template_id)),
          },

          notification_channel: {
            required: helpers.withMessage('Notification method is required',
              requiredIf(_ => this.shouldSendLetter === 'true' && this.edited.engagement_letter.language_id)),
          },

          notification_language: {
            required: helpers.withMessage('Notification language is required',
              requiredIf(_ => this.shouldSendLetter === 'true' && this.edited.engagement_letter.recipient_identifier)),
          },

          recipient_identifier: {
            required: helpers.withMessage('Recipient is required',
              requiredIf(_ => this.shouldSendLetter === 'true' && this.edited.engagement_letter.notification_channel)),
          },

          template_id: {
            required: helpers.withMessage('Template is required',
              requiredIf(_ => this.shouldSendLetter === 'true')),
          },
        },
      },

      shouldSendLetter: {
        required: helpers.withMessage('Please select an option',
          requiredIf(_ => !this.sendMode)),
      },
    }
  },

  watch: {
    'edited.engagement_letter.language_id': {
      handler (value) {
        if (value) {
          // Set engagement letter content from selected template
          this.setEngagementLetterContent()
        } else {
          // Clear dependent fields when language is cleared
          this.edited.engagement_letter.notification_channel = null
          this.edited.engagement_letter.notification_language = null
          this.edited.engagement_letter.content = null
        }
      },

      immediate: true,
    },

    'edited.engagement_letter.notification_channel' () {
      this.edited.engagement_letter.recipient_identifier = null
    },

    'edited.engagement_letter.notification_language' (value) {
      this.edited.engagement_letter.notification_message = null
      if (!value) return

      this.edited.engagement_letter.notification_message = this.notificationContent
    },

    'edited.engagement_letter.recipient_identifier' () {
      this.edited.engagement_letter.notification_language = null
    },

    'edited.parties' () {
      const partyIds = (this.edited.parties || []).
        reduce((res, party) => {
          if (party?.id) res.push(party.id)
          return res
        }, [])

      if (!partyIds.length) {
        this.parties = this.edited.parties || []
        return
      }

      // We are fetching additional data for existing parties that have been selected,
      // i.e. phones and emails
      this.getPartiesWithOptions({ options: { include: ['emails', 'phones'] }, partyIds }).
        then(parties => {
          // We are matching the fetched data with the edited.parties data and we update
          // the edited.parties data to ease the rest of the flow
          this.parties = this.edited.parties.map(party => {
            const existingParty = parties.find(p => p.id === party.id)
            if (existingParty) {
              party.primary_text_number = existingParty.primary_text_number
              party.primary_email = existingParty.primary_email
            }

            return party
          })
        })
    },

    hasOrganizationError () {
      // Update form validity when organization error status changes
      this.$emit('update:valid', this.isFormValid)
    },

    isInitializing (value) {
      this.$emit('update:loading', value)
    },

    organizationName (value) {
      // Update organization name in engagement letter when primary attorney changes
      if (this.shouldDisplayForm && value) {
        this.$set(this.edited.engagement_letter, 'organization_name', value)
        // Also update the content with the new organization name
        this.setEngagementLetterContent()
      }
    },

    primaryAttorney (value) {
      // When primaryAttorney loads, finish initialization if still loading
      if (this.isInitializing && (value !== undefined)) {
        this.isInitializing = false
      }
    },

    sendMode: {
      handler (newValue, oldValue) {
        this.$set(this.edited, 'engagement_letter', Object.assign({}, ENGAGEMENT_LETTER))
        this.v$.edited.engagement_letter.$reset()

        if (newValue) {
          this.shouldSendLetter = 'true'
        } else if (oldValue === true) {
          this.shouldSendLetter = 'false'
        }
      },
    },

    shouldSendLetter (value) {
      // Always reset engagement letter when changing mode
      this.$set(this.edited, 'engagement_letter', Object.assign({}, ENGAGEMENT_LETTER))

      // Handle engagement letter data changes
      if (value === 'true') {
        // Set organization name from primary attorney when form is shown
        if (this.organizationName) {
          this.$set(this.edited.engagement_letter, 'organization_name', this.organizationName)
        }
      } else {
        // Also clear validation state
        this.v$.edited.engagement_letter.$reset()
      }

      // Emit validity change when shouldSendLetter changes
      this.$nextTick().then(_ => {
        this.$emit('update:valid', this.isFormValid)
      })
    },

    'v$.$invalid' () {
      this.$emit('update:valid', this.isFormValid)
    },
  },

  async created () {
    this.getFeatures()
    this.getEmailNotificationEnabled()
    this.getLanguagesModel()
    await this.getEngagementLetterTemplates()

    // Wait a bit for asyncComputed primaryAttorney to load
    // This ensures we have all the data before showing validation messages
    await this.$nextTick()

    if (this.sendMode) {
      this.shouldSendLetter = 'true'
    }

    // Emit initial validity state after everything is set up
    this.$nextTick().then(_ => {
      this.$emit('update:valid', this.isFormValid)
    })
  },

  methods: {
    getErrorMessages (validator) {
      if (!validator || !validator.$errors) return []
      return validator.$errors.map(e => e.$message)
    },

    onTemplateChange () {
      this.edited.engagement_letter.language_id = null
    },

    partyCanBeRecipient (party) {
      if (this.edited.engagement_letter.notification_channel === 'email') return this.partyHasEmail(party)
      if (this.edited.engagement_letter.notification_channel === 'text') return this.partyHasPreferredTextNumber(party)
    },

    partyHasEmail (party) {
      return !isEmpty(party.primary_email?.address)
    },

    partyHasPreferredTextNumber (party) {
      // TODO Remove once whatsapp support fully works
      return !isEmpty(party.primary_text_number?.phone_number) && party.primary_text_number.phone_type !== 'wa'
    },

    setEngagementLetterContent () {
      if (!this.selectedTemplate || !this.edited.engagement_letter.language_id) return

      const translation = this.selectedTemplate.engagement_letter_template_translations?.find(
        t => t.language_id === this.edited.engagement_letter.language_id
      )

      if (!translation) return

      let content = translation.content || ''

      // Replace organization placeholder if we have organization name
      if (this.organizationName) {
        content = content.replace(/\[Organization\]/g, this.organizationName)
      }

      this.$set(this.edited.engagement_letter, 'content', content)
    },

    validate () {
      this.v$.$touch()
      return !this.v$.$invalid
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'getEmailNotificationEnabled',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'getPartiesWithOptions',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
    ]),

    ...mapActions(USER_NAMESPACE, [
      'getUser',
    ]),

    ...mapActions(ENGAGEMENT_LETTER_TEMPLATE_NAMESPACE, [
      'getEngagementLetterTemplates',
    ]),

    getLanguagesModel () {
      return this.$store.dispatch('getLanguagesModel')
    },

    ...alertHandler(),
  },
}
</script>
